// Route guard quyền cho hub SaaS — lớp phòng thủ THÊM, không phải nguồn sự thật.
// Nguồn sự thật là requirePermission ở BE; guard này chỉ để user không gõ URL vào được trang
// trắng/403. Trước 2026-07-15 SaaS KHÔNG có guard nào: <RequireAuth> (App.jsx) chỉ check token
// nên ẩn menu là ảo giác — gõ thẳng URL là vào (audit P0-A).
//
// KHÁC Core (attendance-app/admin/src/navigation/adminMenu.js:506):
//   1. Core giữ PATH_TO_MODULE — một map TAY, tách rời menu → drift. SaaS derive thẳng từ
//      NAV_GROUPS (mỗi item đã có sẵn field `permission`) ⇒ menu và route KHÔNG THỂ lệch nhau.
//   2. Core FAIL-OPEN (`if (!matchedPath) return true`) → path lạ lọt hết. SaaS FAIL-CLOSED:
//      không khớp gì → CHẶN. Bù lại phải khai báo tường minh ALWAYS_ALLOWED + EXTRA_GATES dưới.
//
// ⚠️ Thêm <Route> mới vào App.jsx mà path không nằm dưới path menu nào thì PHẢI thêm vào
// ALWAYS_ALLOWED hoặc EXTRA_GATES, nếu không user thường sẽ bị đá về /dashboard.
// tools/check-route-guard-coverage.js gác việc này.

// Route cần đăng nhập nhưng KHÔNG gắn quyền — self-service, điều hướng, hoặc trang lỗi.
// Regex khớp TOÀN BỘ pathname.
export const ALWAYS_ALLOWED = [
  /^\/$/,                       // index → redirect /dashboard
  /^\/dashboard$/,              // trang chủ, mọi vai trò
  /^\/select-workspace$/,       // chọn workspace (chưa có active tenant)
  /^\/setting-up$/,             // màn hình chờ provisioning
  /^\/me$/,                     // hồ sơ cá nhân
  /^\/profile(\/.*)?$/,         // hồ sơ cá nhân + /profile/payroll = phiếu lương CỦA MÌNH
  // «Lịch làm việc và ca trống» của CHÍNH MÌNH — tự phục vụ, cố ý KHÔNG có mục menu (vào từ
  // trang cá nhân) nên không derive được path nào. Thiếu dòng này thì guard fail-closed đá
  // nhân viên thường về /dashboard, trong khi đây đúng là màn dành cho họ: nhận ca trống là
  // MỘT trong tám cửa vào tăng ca (`open_shift_claim`). Admin '*' bypass nên bug không lộ khi
  // thử bằng tài khoản admin. Phạm vi vẫn do BE gác (`requireSelfScope` ở /attendance-roster/*).
  /^\/my-roster$/,
  /^\/notifications(\/.*)?$/,   // thông báo cá nhân
  // «Tin mới» từ CNV (chuông trên thanh trên cùng) — mọi người dùng đọc được, không gắn quyền;
  // không có mục menu nên không derive được path. BE: /api/v1/product-news (chỉ đọc + dòng đọc của mình).
  /^\/whats-new(\/.*)?$/,
  /^\/chat(\/.*)?$/,            // chat nội bộ — gate bằng private_modules.team_chat (menu lo)
  /^\/reports(\/.*)?$/,         // nhóm Báo cáo cố ý KHÔNG gate (memory project_rbac_inbox_1to1)
  /^\/support(\/.*)?$/,         // ticket: nhân viên tự tạo/xem ticket của mình
  /^\/automation(\/.*)?$/,      // menu single-link không gắn permission
  /^\/suppliers(\/.*)?$/,       // menu Tiện ích không gắn permission
];

// Path KHÔNG có menu item (nên không derive được) nhưng VẪN phải gate.
// payroll: từ 20/09/2026 nhóm «Lương» đã mở lại ở AppLayout nên 5 path con derive được; giữ
// dòng '/payroll' làm lưới chắn cho path con nào mount sau mà quên thêm mục menu (fail-closed
// vẫn chặn, nhưng chặn bằng ĐÚNG quyền thì hơn là đá về /dashboard).
// KHÔNG gồm /profile/payroll (phiếu lương của chính mình — đã ở ALWAYS_ALLOWED).
export const EXTRA_GATES = {
  '/payroll': 'payroll:view',
  '/finance/domains': 'domains:view',
  // Alias Thư viện Pipeline. Menu trỏ /settings/pipelines (đúng Core), nhưng App.jsx vẫn mount
  // /recruitment/pipelines/* cho link/bookmark cũ. Không khai ở đây thì longest-prefix rơi về
  // menu item '/recruitment' → gate nhầm bằng recruitment_dashboard:view.
  '/recruitment/pipelines': 'recruitment_pipelines:view',
  // Alias Kho ứng dụng. Menu đổi sang /billing ngày 29.08.2026 (trang gom cả Billing vào), nhưng
  // App.jsx VẪN mount /marketplace + /marketplace/orders/:id cho link đã gửi khách, bookmark và
  // task của sales. Không khai ở đây thì fail-closed chặn thẳng user thường ngay lúc họ bấm link
  // trả tiền — mà admin ('*' bypass) không bao giờ thấy. Cùng quyền với mục menu /billing.
  '/marketplace': 'marketplace:view',
};

/**
 * Derive [{ path, permission }] từ NAV_GROUPS, sắp path DÀI TRƯỚC để longest-prefix thắng
 * (vd /crm/leads phải thắng /crm).
 */
export function buildRouteMap(navGroups) {
  const out = [];
  const push = (path, permission) => { if (path && permission) out.push({ path, permission }); };
  for (const g of navGroups || []) {
    push(g.path, g.permission);
    for (const it of g.items || []) push(it.path, it.permission);
  }
  for (const [path, permission] of Object.entries(EXTRA_GATES)) push(path, permission);
  return out.sort((a, b) => b.path.length - a.path.length);
}

/**
 * FAIL-CLOSED.
 * @param {string}   pathname
 * @param {Array}    routeMap  — từ buildRouteMap(), đã sắp dài-trước
 * @param {Function} hasPerm   — (permission) => boolean; dùng CHUNG hàm đang lọc menu để guard và
 *                               menu không thể lệch logic (admin bypass, '*' bypass)
 * @param {boolean}  isAdmin   — bypass toàn bộ (khớp BE: requirePermission cho admin qua thẳng)
 */
export function checkRoutePermission(pathname, routeMap, hasPerm, isAdmin) {
  if (!pathname) return false;
  if (isAdmin) return true;
  if (ALWAYS_ALLOWED.some((rx) => rx.test(pathname))) return true;

  const hit = routeMap.find((r) => pathname === r.path || pathname.startsWith(`${r.path}/`));
  if (!hit) return false;            // ⛔ fail-closed — KHÁC Core (Core: return true)
  return hasPerm(hit.permission);
}
